import { InstallSource } from "@redotech/account-common/install-source";
import { FeatureFlagKey } from "@redotech/feature-flags-model/keys";
import { isRedoEmailAddress } from "@redotech/redo-model/redo-domains";
import { RECLAIM_INSTALL_APP_PROMPT_DEFER_HOURS } from "./constants";

const RECLAIM_INSTALL_APP_EMPLOYEE_SUPPRESSION_BYPASS_EMAILS = new Set([
  "ben.woodward@getredo.com",
  "benjamin@redo.com",
  "dev@getredo.com",
]);

interface ReclaimPromptDisputesSettings {
  enabled?: boolean | null;
  automateEvidenceEnabled?: boolean | null;
  reclaimShopifyAppInstalledAt?: Date | string | null;
  reclaimShopifyAppInstallPromptDeferredUntil?: Date | string | null;
}

export interface ReclaimPromptTeam {
  installSource?: InstallSource | null;
  platform?: string | null;
  featureFlags?: Partial<Record<FeatureFlagKey, boolean | undefined>> | null;
  settings?: { disputes?: ReclaimPromptDisputesSettings | null } | null;
}

function parseDate(value: Date | string | null | undefined): Date | null {
  if (!value) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function canBypassRedoEmployeeSuppression(
  email: string | null | undefined,
): boolean {
  const normalizedEmail = email?.trim().toLowerCase();
  return (
    normalizedEmail != null &&
    RECLAIM_INSTALL_APP_EMPLOYEE_SUPPRESSION_BYPASS_EMAILS.has(normalizedEmail)
  );
}

export function hasInstalledReclaimShopifyApp(
  team: ReclaimPromptTeam,
): boolean {
  return (
    team.installSource === InstallSource.RECLAIM_APP ||
    team.settings?.disputes?.reclaimShopifyAppInstalledAt != null
  );
}

export function getReclaimInstallAppPromptDeferredUntil(
  now = new Date(),
): Date {
  return new Date(
    now.getTime() + RECLAIM_INSTALL_APP_PROMPT_DEFER_HOURS * 60 * 60 * 1000,
  );
}

export function shouldShowReclaimInstallAppPrompt({
  currentUserEmail,
  team,
  now = new Date(),
}: {
  currentUserEmail?: string | null;
  team: ReclaimPromptTeam | null | undefined;
  now?: Date;
}): boolean {
  if (!team) {
    return false;
  }

  if (
    isRedoEmailAddress(currentUserEmail) &&
    !canBypassRedoEmployeeSuppression(currentUserEmail)
  ) {
    return false;
  }

  if (team.featureFlags?.[FeatureFlagKey.RECLAIM_INSTALL_APP_MODAL] !== true) {
    return false;
  }

  if (team.platform !== "shopify") {
    return false;
  }

  if (team.settings?.disputes?.enabled !== true) {
    return false;
  }

  if (team.settings.disputes.automateEvidenceEnabled !== true) {
    return false;
  }

  if (hasInstalledReclaimShopifyApp(team)) {
    return false;
  }

  const deferredUntil = parseDate(
    team.settings.disputes.reclaimShopifyAppInstallPromptDeferredUntil,
  );
  if (deferredUntil && deferredUntil.getTime() > now.getTime()) {
    return false;
  }

  return true;
}
